import {
    useCallback,
    useDeferredValue,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { Grid } from '~/engine/data/common/Grid';
import { DicomImageSlice } from '~/domain/dicom/dicomImageSlice';
import { Modality } from '~/domain/dicom/modality';
import { computeMedianSpacing } from '~/domain/services/assembleVolume';
import {
    buildReformatGrid,
    clampRegionCentre,
    defaultRegionForSlices,
    gridBoundsMm,
    paddedFractionInBox,
    reformatTargetModality,
    buildExtendedReformatGrid,
    excludedByExtensionMm,
    extendedMarginMm,
    reformatRegionRefusal,
    nextFreeSeriesNumber,
    reformatSourceBoundsMm,
    reformatSourceBoxMm,
    reformatSourceSlices,
    replicatedSliceCount,
    MIN_FOV_MM,
    MAX_FOV_MM,
    MAX_EXTENSION_MARGIN_MM,
    MIN_EXTENSION_MARGIN_MM,
    DEFAULT_EXTENSION_MARGIN_MM,
    MIN_SLICES,
    shiftWithin,
    MIN_THICKNESS_MM,
    MAX_THICKNESS_MM,
    NO_EXTENSION,
} from '~/domain/services/imageService';
import type {
    ReformatBlockedReason,
    ReformatExtension,
    ReformatRegion,
    ReformatTarget,
    WorldBoundsMm,
    OrientedBoxMm,
} from '~/domain/services/imageService';
import {
    validateSlicePositions,
    SliceValidationStatus,
} from '~/domain/patient/image3d';
import { obliquityAngleDeg } from '~/utils/obliquity';

/** Matrix sizes always offered, alongside the source's own. */
export const MATRIX_SIZE_CHOICES = [256, 512, 1024];

export interface ReformatSourceGeometry {
    /** In-plane voxel size, mm. */
    pixelSpacingMm: [number, number];
    /** Median centre-to-centre slice spacing, mm. */
    sliceSpacingMm: number;
    /** Voxel counts of one slice. */
    cols: number;
    rows: number;
    /** False when the slices are too irregular to stack without resampling. */
    isUniform: boolean;
    isTilted: boolean;
    tiltDeg: number;
}

/**
 * A translation of the region, mm, on whichever axes the control moves.
 *
 * One shape for every mover: a scout moves one in-plane axis and Z, the
 * output preview moves both in-plane axes and no Z. Naming the axes rather
 * than passing an axis selector keeps the clamping in one place instead of
 * one per caller.
 */
export interface RegionDeltaMm {
    xMm?: number;
    yMm?: number;
    zMm?: number;
}

export interface ReformatSource {
    /** The target these facts were derived from. */
    target: ReformatTarget;
    slices: DicomImageSlice[];
    modality: Modality;
    seriesDescription: string;
    /** Default output description, already localized. */
    defaultOutputDescription: string;
    /** The region covering the whole source — the default selection. */
    region: ReformatRegion;
    /** The source's own world box — the scouts' extent. */
    boundsMm: WorldBoundsMm;
    /**
     * The source's own parallelepiped, against which padding is measured.
     * Not the box above: for an oblique source the two differ by exactly
     * the corner wedges a reformat pads.
     */
    boxMm: OrientedBoxMm;
    /** Native in-plane matrix size, offered alongside the fixed choices. */
    matrixSize: number;
    geometry: ReformatSourceGeometry;
}

/**
 * The source facts a reformat is configured against, derived once per
 * target.
 *
 * Both target shapes are read through their slices, so a raw series and
 * the same acquisition once loaded produce the same region — see
 * {@link defaultRegionForSlices} for why a grid's bounding box would not.
 */
export function useReformatSource(target: ReformatTarget): ReformatSource {
    const intl = useIntl();

    return useMemo(() => {
        const slices = reformatSourceSlices(target);
        if (slices.length === 0) {
            throw new Error(
                'Cannot configure a reformat: the target has no image slices'
            );
        }
        const firstSlice = slices[0];

        const seriesDescription =
            (target.kind === 'rawSeries'
                ? target.series.seriesModule.seriesDescription
                : target.image.seriesMetadata.seriesDescription) || '';

        // A raw series is exactly the case where stacking may not be
        // regular; a loaded image already passed that check to exist.
        const isUniform =
            target.kind === 'loadedImage' ||
            validateSlicePositions(target.series).status ===
                SliceValidationStatus.CompleteReconstruction;

        const orientation = firstSlice.imageOrientation;

        return {
            target,
            slices,
            modality: reformatTargetModality(target),
            seriesDescription:
                seriesDescription ||
                intl.formatMessage({ id: 'unknown.series' }),
            defaultOutputDescription: intl.formatMessage({
                id: 'reformatDefaultDescription',
            }),
            region: defaultRegionForSlices(slices),
            boundsMm: reformatSourceBoundsMm(target),
            boxMm: reformatSourceBoxMm(target),
            matrixSize: Math.max(firstSlice.cols, firstSlice.rows),
            geometry: {
                pixelSpacingMm: [
                    firstSlice.pixelSpacing[0],
                    firstSlice.pixelSpacing[1],
                ],
                sliceSpacingMm: computeMedianSpacing(slices),
                cols: firstSlice.cols,
                rows: firstSlice.rows,
                isUniform,
                isTilted: firstSlice.tilted,
                tiltDeg: obliquityAngleDeg(orientation),
            },
        };
    }, [target, intl]);
}

export interface ReformatParameters {
    region: ReformatRegion;
    matrixSize: number;
    sliceThicknessMm: number;
    outputDescription: string;
    seriesNumber: number;
    /** True when the chosen number is already taken by a loaded series. */
    seriesNumberTaken: boolean;
    setSeriesNumber: (seriesNumber: number) => void;
    /**
     * The grid the reformat will produce. Every figure shown to the user
     * is read off this, so the panel and the output cannot disagree.
     */
    outputGrid: Grid;
    /** Fraction of the output that will be padding, in [0, 1]. */
    paddedFraction: number;
    /**
     * True while {@link paddedFraction} still describes an earlier region
     * than the one the reformat would now produce.
     *
     * The figure is deferred so a drag stays smooth, which means it can
     * lag the region by a render or more. It is stated beside the control
     * that starts the reformat, so for as long as it lags, the proportion
     * on screen is not the proportion the output would record — the
     * operator would be accepting a number for a different geometry. The
     * caller blocks the start until this clears, which it does as soon as
     * the deferred render lands.
     */
    paddedFractionStale: boolean;
    extension: ReformatExtension;
    /** Output slices that will be copies of a boundary slice. */
    replicatedSlices: number;
    /** The margin those slices actually span at each end, mm. */
    extensionMarginMm: number;
    /**
     * Why the reformat cannot be started with the current parameters, or
     * null. Mirrors what {@link executeReformat} would refuse.
     */
    startBlockedReason: ReformatBlockedReason | null;
    /**
     * Measured image data the extension will replace with replicas, mm —
     * non-zero only where the slice range has been cropped inward at an
     * extended end.
     */
    excludedMm: number;
    /**
     * Turn the extension on or off as a whole. On extends BOTH ends at the
     * default margin, because a short series is usually short for the same
     * reason at each end and an extension enabled at neither end would do
     * nothing while appearing to be on.
     */
    setExtensionEnabled: (enabled: boolean) => void;
    setExtensionMarginMm: (marginMm: number) => void;
    setExtendSuperior: (extend: boolean) => void;
    setExtendInferior: (extend: boolean) => void;
    setFovMm: (fovMm: number) => void;
    /**
     * Set the field of view from one in-plane axis's [low, high] edges.
     *
     * The field of view is square, so the width taken from `axis` applies
     * to BOTH in-plane axes: dragging a coronal edge also changes how wide
     * the box is on the sagittal view. Only the dragged axis's centre
     * moves; the other keeps its own.
     */
    setInPlaneBounds: (axis: 'x' | 'y', bounds: [number, number]) => void;
    /**
     * Translate the region without resizing it: the in-plane centre on the
     * dragged axis, and the slice range, both by a world-mm delta.
     *
     * Edges reshape the region; this repositions it. Keeping the two apart
     * is what lets a symmetric box stay symmetric while it is moved.
     */
    moveRegionBy: (delta: RegionDeltaMm) => void;
    setZRange: (zRangeMm: [number, number]) => void;
    setMatrixSize: (matrixSize: number) => void;
    setSliceThicknessMm: (sliceThicknessMm: number) => void;
    setOutputDescription: (outputDescription: string) => void;
}

/**
 * The editable reformat parameters and the output grid they describe.
 *
 * Edits that would leave an unusable geometry are refused rather than
 * corrected, so a drag simply stops at the limit instead of the value
 * jumping somewhere the user did not put it.
 */
export function useReformatParameters(
    source: ReformatSource,
    open: boolean,
    usedSeriesNumbers: ReadonlySet<number>
): ReformatParameters {
    const initialThicknessMm = Math.min(
        MAX_THICKNESS_MM,
        Math.max(source.geometry.sliceSpacingMm, MIN_THICKNESS_MM)
    );

    const [region, setRegion] = useState<ReformatRegion>(source.region);
    const [matrixSize, setMatrixSize] = useState(source.matrixSize);
    const [sliceThicknessMm, setSliceThicknessMm] =
        useState(initialThicknessMm);
    const [outputDescription, setOutputDescription] = useState(
        source.defaultOutputDescription
    );
    const [extension, setExtension] = useState<ReformatExtension>(NO_EXTENSION);
    const [seriesNumber, setSeriesNumber] = useState<number>(() =>
        nextFreeSeriesNumber(usedSeriesNumbers)
    );
    // Read at reset time rather than depended on: a series arriving in the
    // session while the dialog is open must not wipe what the user has set.
    const usedSeriesNumbersRef = useRef(usedSeriesNumbers);
    usedSeriesNumbersRef.current = usedSeriesNumbers;

    // Reopening the dialog starts from the source again: the previous
    // run's parameters belong to whatever target it was opened on.
    useEffect(() => {
        if (open) {
            setRegion(source.region);
            setMatrixSize(source.matrixSize);
            setSliceThicknessMm(initialThicknessMm);
            setOutputDescription(source.defaultOutputDescription);
            setExtension(NO_EXTENSION);
            setSeriesNumber(nextFreeSeriesNumber(usedSeriesNumbersRef.current));
        }
    }, [open, source, initialThicknessMm]);

    // The source extent the in-plane centre is held against. Read once so
    // every setter that can move the region clamps against the same box.
    const sourceBoundsMm = source.boundsMm;

    const setFovMm = useCallback(
        (fovMm: number) => {
            setRegion((current) =>
                fovMm < MIN_FOV_MM || fovMm > MAX_FOV_MM
                    ? current
                    : clampRegionCentre({ ...current, fovMm }, sourceBoundsMm)
            );
        },
        [sourceBoundsMm]
    );

    const setInPlaneBounds = useCallback(
        (axis: 'x' | 'y', bounds: [number, number]) => {
            const [low, high] = bounds;
            const fovMm = high - low;
            if (fovMm < MIN_FOV_MM || fovMm > MAX_FOV_MM) {
                return;
            }
            const center = (low + high) / 2;
            setRegion((current) =>
                clampRegionCentre(
                    {
                        ...current,
                        fovMm,
                        centerXMm: axis === 'x' ? center : current.centerXMm,
                        centerYMm: axis === 'y' ? center : current.centerYMm,
                    },
                    sourceBoundsMm
                )
            );
        },
        [sourceBoundsMm]
    );

    // Thickness and range constrain each other: raising the thickness on a
    // narrow range would otherwise leave a one-slice output, which the
    // range control alone cannot prevent.
    const setSliceThickness = useCallback(
        (thicknessMm: number) => {
            // Refused rather than clamped, as `setFovMm` and
            // `setExtensionMarginMm` refuse their own bounds. The input
            // carries the same limits, but this hook is the guard every
            // caller passes through, and the bounds are a property of the
            // parameter rather than of the one control that happens to
            // edit it. A caller needing the nearest allowed value clamps
            // before calling, the way the preview's wheel does for the
            // field of view.
            if (
                thicknessMm < MIN_THICKNESS_MM ||
                thicknessMm > MAX_THICKNESS_MM
            ) {
                return;
            }
            const [z0, z1] = region.zRangeMm;
            if (z1 - z0 < (MIN_SLICES - 1) * thicknessMm) {
                return;
            }
            setSliceThicknessMm(thicknessMm);
        },
        [region.zRangeMm]
    );

    const sourceZRange = source.region.zRangeMm;
    const moveRegionBy = useCallback(
        (delta: RegionDeltaMm) => {
            setRegion((current) =>
                clampRegionCentre(
                    {
                        ...current,
                        centerXMm: current.centerXMm + (delta.xMm ?? 0),
                        centerYMm: current.centerYMm + (delta.yMm ?? 0),
                        // The range keeps its length, so the slice count
                        // and the thickness are untouched by a move. It is
                        // held inside the source in Z, as every other Z
                        // control is: the field of view may reach past the
                        // acquisition and be padded, but the slice range
                        // may not — the extension is the sanctioned way to
                        // go beyond it, and it says so on the output.
                        zRangeMm: shiftWithin(
                            current.zRangeMm,
                            delta.zMm ?? 0,
                            sourceZRange
                        ),
                    },
                    sourceBoundsMm
                )
            );
        },
        [sourceZRange, sourceBoundsMm]
    );

    const setZRange = useCallback(
        (zRangeMm: [number, number]) => {
            setRegion((current) => {
                const [z0, z1] = zRangeMm;
                // MIN_SLICES slice centres span MIN_SLICES - 1 gaps.
                if (z1 - z0 < (MIN_SLICES - 1) * sliceThicknessMm) {
                    return current;
                }
                return { ...current, zRangeMm: [z0, z1] };
            });
        },
        [sliceThicknessMm]
    );

    const setExtensionEnabled = useCallback((enabled: boolean) => {
        setExtension((current) => ({
            marginMm: enabled
                ? current.marginMm || DEFAULT_EXTENSION_MARGIN_MM
                : current.marginMm,
            superior: enabled,
            inferior: enabled,
        }));
    }, []);

    const setExtensionMarginMm = useCallback((marginMm: number) => {
        setExtension((current) =>
            marginMm < MIN_EXTENSION_MARGIN_MM ||
            marginMm > MAX_EXTENSION_MARGIN_MM
                ? current
                : { ...current, marginMm }
        );
    }, []);

    const setExtendSuperior = useCallback((superior: boolean) => {
        setExtension((current) => ({ ...current, superior }));
    }, []);

    const setExtendInferior = useCallback((inferior: boolean) => {
        setExtension((current) => ({ ...current, inferior }));
    }, []);

    const replicatedSlices = replicatedSliceCount(extension, sliceThicknessMm);

    // The output covers the selected region plus whatever the extension
    // adds, so the panel's figures already include the replicated slices.
    const outputGrid = useMemo(
        () =>
            buildExtendedReformatGrid(
                region,
                matrixSize,
                sliceThicknessMm,
                extension
            ).grid,
        [region, matrixSize, sliceThicknessMm, extension]
    );

    // Extending from a cropped end replaces data that exists with copies of
    // one slice. How much is the user's to weigh, so it is measured here
    // rather than assumed to be nothing.
    const excludedMm = useMemo(
        () =>
            excludedByExtensionMm(
                source.region.zRangeMm,
                region,
                sliceThicknessMm,
                extension
            ),
        [source.region.zRangeMm, region, sliceThicknessMm, extension]
    );

    // Measured on the SELECTED region, not the extended output: the
    // extension's slices are replicas, not fill, and the reformat records
    // the proportion the same way.
    //
    // Deferred, because it samples a lattice and a drag changes the region
    // far more often than the figure is read. React keeps the previous
    // value visible while a drag is in flight and recomputes once it
    // settles.
    //
    // While it lags, the figure on screen belongs to an EARLIER region
    // than the one the start control would act on, and this figure is
    // stated beside that control. `paddedFractionStale` reports the lag so
    // the caller can hold the start until it clears; without that, a drag
    // followed promptly by a click showed one proportion and recorded
    // another. Once settled the two agree by construction —
    // `executeReformat` computes it again, the same function of the same
    // region.
    const settledRegion = useDeferredValue(region);
    // Reference equality is the right test. A refused edit returns the
    // same object and React bails out, so it never reads as stale; an
    // accepted one always builds a new object, so a region dragged back to
    // the numbers it started on reads as stale for one render even though
    // the figure will not change. That errs towards making the operator
    // wait, which is the safe direction for a number they are about to act
    // on.
    const paddedFractionStale = settledRegion !== region;
    const padded = useMemo(
        () =>
            paddedFractionInBox(
                gridBoundsMm(
                    buildReformatGrid(
                        settledRegion,
                        matrixSize,
                        sliceThicknessMm
                    )
                ),
                source.boxMm
            ),
        [settledRegion, matrixSize, sliceThicknessMm, source.boxMm]
    );

    // The same predicate executeReformat refuses on, so the control and the
    // operation cannot disagree about what is possible. Memoized because it
    // walks every source slice to measure the source box, and the dialog
    // re-renders on every drag frame.
    const startBlockedReason = useMemo(
        () =>
            reformatRegionRefusal(
                source.target,
                region,
                matrixSize,
                sliceThicknessMm,
                extension
            ),
        [source.target, region, matrixSize, sliceThicknessMm, extension]
    );

    return {
        region,
        matrixSize,
        sliceThicknessMm,
        outputDescription,
        seriesNumber,
        seriesNumberTaken: usedSeriesNumbers.has(seriesNumber),
        setSeriesNumber,
        outputGrid,
        paddedFraction: padded,
        paddedFractionStale,
        extension,
        replicatedSlices,
        extensionMarginMm: extendedMarginMm(extension, sliceThicknessMm),
        startBlockedReason,
        excludedMm,
        setExtensionEnabled,
        setExtensionMarginMm,
        setExtendSuperior,
        setExtendInferior,
        setFovMm,
        setInPlaneBounds,
        moveRegionBy,
        setZRange,
        setMatrixSize,
        setSliceThicknessMm: setSliceThickness,
        setOutputDescription,
    };
}
